import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api/api';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { format } from 'date-fns';
import {
    Loader2,
    X,
    CheckCircle2,
    XCircle,
    Clock,
    Zap,
    Target,
    Play,
    Info,
    ArrowRight,
    Fingerprint,
    Server,
    ShieldAlert
} from 'lucide-react';

const RunInspector = ({ runId, onClose }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchRunDetails = useCallback(async () => {
        try {
            setLoading(true);
            const res = await api.get(`/api/inbound-automations/runs/${runId}`);
            setData(res.data);
        } catch (err) {
            console.error('Failed to fetch run details:', err);
        } finally {
            setLoading(false);
        }
    }, [runId]);

    useEffect(() => {
        if (runId) fetchRunDetails();
    }, [runId, fetchRunDetails]);

    if (loading) {
        return (
            <div className="h-full flex flex-col items-center justify-center p-10 bg-card/50 backdrop-blur-xl">
                <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
                <p className="text-sm font-bold text-muted-foreground uppercase tracking-widest">Inspecting Run...</p>
            </div>
        );
    }

    if (!data) return null;

    const { run, history } = data;
    const signal = run.context || {};

    return (
        <div className="h-full flex flex-col bg-background/95 backdrop-blur-md shadow-2xl border-l border-border/50 animate-in slide-in-from-right duration-300">
            {/* Header */}
            <div className="p-6 border-b border-border/50 bg-muted/20">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <Badge variant="outline" className="bg-primary/5 text-primary border-primary/20 font-mono text-[9px] uppercase">
                            Run ID: {run._id.substring(0, 12)}...
                        </Badge>
                        <Badge className={`${run.status === 'COMPLETED' ? 'bg-green-500' : run.status === 'FAILED' ? 'bg-red-500' : run.status === 'SUPPRESSED' ? 'bg-amber-500' : 'bg-slate-500'} text-white text-[9px] font-black`}>
                            {run.status}
                        </Badge>
                    </div>
                    <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0 rounded-full hover:bg-muted-foreground/10">
                        <X className="h-4 w-4" />
                    </Button>
                </div>
                <h2 className="text-xl font-black text-foreground tracking-tight leading-none mb-1">
                    {run.signalType} Execution
                </h2>
                <p className="text-[11px] text-muted-foreground font-medium uppercase tracking-tighter">
                    Triggered {format(new Date(run.createdAt), 'MMM d, HH:mm:ss')}
                </p>
            </div>

            <div className="flex-1 overflow-auto p-6 space-y-8">
                {run.status === 'SUPPRESSED' && (
                    <div className="bg-amber-500/10 border-2 border-amber-500/20 rounded-2xl p-4 flex gap-3 animate-pulse">
                        <ShieldAlert className="h-5 w-5 text-amber-600 shrink-0" />
                        <div>
                            <h5 className="text-[11px] font-black uppercase tracking-widest text-amber-700">Signal Suppressed</h5>
                            <p className="text-[10px] font-medium text-amber-600/80 leading-relaxed">
                                This event was detected as a duplicate. Previous execution (Run ID: {run.context?.originalRunId?.substring(0,8)}...) already handled this signal. No further actions were taken to prevent double-processing.
                            </p>
                        </div>
                    </div>
                )}

                {/* 1. Signal Card */}
                <section>
                    <div className="flex items-center gap-2 mb-4">
                        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                            <Zap className="h-4 w-4 text-primary" />
                        </div>
                        <h4 className="text-[11px] font-black uppercase tracking-widest text-muted-foreground">The Signal</h4>
                    </div>
                    
                    <div className="bg-card rounded-2xl border border-border/50 shadow-sm overflow-hidden flex flex-col">
                        <div className="p-4 grid grid-cols-2 gap-4 bg-muted/10 border-b border-border/30">
                            <div>
                                <span className="text-[9px] font-black uppercase tracking-tighter text-muted-foreground block mb-0.5">Source Type</span>
                                <div className="flex items-center gap-1.5 text-[11px] font-bold text-foreground capitalize">
                                    <Server className="h-3 w-3 opacity-50" />
                                    {run.sourceType?.replace('_', ' ') || 'SYSTEM'}
                                </div>
                            </div>
                            <div>
                                <span className="text-[9px] font-black uppercase tracking-tighter text-muted-foreground block mb-0.5">Event Identifier</span>
                                <div className="flex items-center gap-1.5 text-[11px] font-bold text-foreground">
                                    <Fingerprint className="h-3 w-3 opacity-50" />
                                    <span className="font-mono text-[10px] truncate">{run.fingerprint?.substring(0, 12)}...</span>
                                </div>
                            </div>
                        </div>
                        <div className="p-4 bg-card">
                            <div className="mb-3">
                                <span className="text-[9px] font-black uppercase tracking-tighter text-muted-foreground block mb-1">Subject / Header</span>
                                <p className="text-[11px] font-medium text-foreground bg-muted/40 p-2 rounded-lg border border-border/50">
                                    {signal.data?.subject || '(No Subject)'}
                                </p>
                            </div>
                            <div>
                                <div className="flex items-center gap-1.5 mb-1">
                                    <span className="text-[9px] font-black uppercase tracking-tighter text-muted-foreground block">Semantic Classification</span>
                                    <Badge variant="ghost" className="text-[8px] h-4 px-1.5 bg-muted/50 text-muted-foreground border-none font-bold italic">Heuristic-based</Badge>
                                </div>
                                <Badge variant="outline" className={`text-[10px] font-black h-6 px-3 border-2 ${signal.data?.classification === 'REPLY' ? 'bg-green-500/5 text-green-600 border-green-500/20' : 'bg-blue-500/5 text-blue-600 border-blue-500/20'}`}>
                                    {signal.data?.classification || 'NORMAL_INBOUND'}
                                </Badge>
                                <p className="text-[8px] text-muted-foreground/50 mt-1.5 leading-tight italic">
                                    * Classification is derived from provider metadata and message semantics.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* 2. Timeline Trace */}
                <section>
                    <div className="flex items-center gap-2 mb-6">
                        <div className="w-8 h-8 rounded-lg bg-indigo-500/10 flex items-center justify-center">
                            <Clock className="h-4 w-4 text-indigo-500" />
                        </div>
                        <h4 className="text-[11px] font-black uppercase tracking-widest text-muted-foreground">Execution Flow Trace</h4>
                    </div>

                    <div className="space-y-0 pl-4 border-l-2 border-border/50 ml-4">
                        {history.map((step, idx) => (
                            <div key={step._id} className="relative pb-10 last:pb-0">
                                {/* Dot Indicator */}
                                <div className={`absolute -left-[27px] w-5 h-5 rounded-full border-4 border-background flex items-center justify-center shadow-md z-10 transition-transform hover:scale-125
                                    ${step.status === 'COMPLETED' ? 'bg-green-500' : 'bg-red-500'}`}>
                                    {step.status === 'COMPLETED' ? (
                                        <CheckCircle2 className="h-2 w-2 text-white" />
                                    ) : (
                                        <XCircle className="h-2 w-2 text-white" />
                                    )}
                                </div>

                                <div className="bg-card rounded-xl border border-border/50 p-4 hover:border-primary/30 transition-all hover:bg-muted/5 group">
                                    <div className="flex items-center justify-between mb-3 border-b border-border/50 pb-2">
                                        <div className="flex items-center gap-2">
                                            {step.nodeType === 'conditionNode' ? (
                                                <Target className="h-3 w-3 text-amber-500" />
                                            ) : step.nodeType === 'actionNode' ? (
                                                <Play className="h-3 w-3 text-green-500" />
                                            ) : (
                                                <Zap className="h-3 w-3 text-blue-500" />
                                            )}
                                            <span className="text-[10px] font-black uppercase tracking-tighter text-foreground group-hover:text-primary transition-colors italic">
                                                {step.nodeType?.replace('Node', '')} Step
                                            </span>
                                        </div>
                                        <span className="text-[9px] font-medium text-muted-foreground tabular-nums">
                                            +{((new Date(step.finishedAt) - new Date(run.createdAt)) / 1000).toFixed(2)}s
                                        </span>
                                    </div>

                                    {/* node-specific results */}
                                    {step.nodeType === 'conditionNode' ? (
                                        <div className="space-y-2">
                                            <div className="flex items-center gap-2">
                                                <Badge className={`text-[9px] h-5 px-2 font-black border-2 ${step.metadataSnapshot?.matched ? 'bg-green-500/10 text-green-600 border-green-500/20' : 'bg-red-500/10 text-red-600 border-red-500/20'}`}>
                                                    {step.metadataSnapshot?.matched ? 'MATCHED' : 'NOT MATCHED'}
                                                </Badge>
                                                <ArrowRight className="h-3 w-3 text-muted-foreground" />
                                                <span className="text-[10px] font-bold text-muted-foreground leading-none">
                                                    Moving to {step.metadataSnapshot?.matched ? 'YES' : 'NO'} path
                                                </span>
                                            </div>
                                            <div className="p-2 rounded-lg bg-muted/40 border border-border/30">
                                                <span className="text-[9px] font-black text-muted-foreground/60 uppercase block mb-1">Evaluating Logic: {step.metadataSnapshot?.logic}</span>
                                                <div className="space-y-1">
                                                    {(step.metadataSnapshot?.criteria || []).map((c, i) => (
                                                        <div key={i} className="flex items-center gap-1.5 text-[10px] font-medium text-foreground/80">
                                                            <div className="w-1 h-1 rounded-full bg-primary/30" />
                                                            <span className="font-bold underline decoration-dotted">{c.field}</span>
                                                            <span className="text-[8px] italic opacity-60 uppercase">{c.op?.replace('_', ' ')}</span>
                                                            <span className="font-mono text-primary bg-primary/5 px-1 rounded">{c.value}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    ) : step.nodeType === 'actionNode' ? (
                                        <div className="flex items-start gap-4">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <Badge className="text-[9px] h-5 px-2 bg-green-500 text-white font-black">
                                                        EXECUTED
                                                    </Badge>
                                                    <span className="text-[10px] font-black text-foreground uppercase tracking-tight">
                                                        {step.metadataSnapshot?.actionType?.replace('_', ' ')}
                                                    </span>
                                                </div>
                                                <p className="text-[11px] font-medium text-muted-foreground leading-relaxed italic bg-muted/20 p-2 rounded-lg">
                                                    {step.metadataSnapshot?.outcome?.message || 'Action completed successfully.'}
                                                </p>
                                            </div>
                                        </div>
                                    ) : (
                                        <p className="text-[11px] text-muted-foreground">Automation entry point processed.</p>
                                    )}

                                    {step.status === 'FAILED' && (
                                        <div className="mt-4 p-3 rounded-lg bg-red-500/5 border border-red-500/20 flex gap-2">
                                            <Info className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />
                                            <div>
                                                <span className="text-[9px] font-black text-red-600 uppercase block mb-0.5">Runtime Error</span>
                                                <p className="text-[11px] font-bold text-red-500/80 leading-tight">{step.metadataSnapshot?.error || 'Unknown error occurred.'}</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                        
                        {/* Terminal Indicator */}
                        <div className="relative pt-4">
                            <div className="absolute -left-[23px] w-3 h-3 rounded-full bg-muted-foreground/30 border-2 border-background" />
                            <span className="text-[9px] font-black text-muted-foreground/40 uppercase tracking-widest pl-2 italic">
                                End of trace
                            </span>
                        </div>
                    </div>
                </section>
            </div>
            
            {/* Footer / Context */}
            <div className="p-4 bg-muted/30 border-t border-border/50 text-center">
                <p className="text-[9px] text-muted-foreground/60 font-medium">
                    Automations are deterministic based on signal content at time of ingestion.
                </p>
            </div>
        </div>
    );
};

export default RunInspector;
